import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';

export interface ForkingQualityDegradedPayload {
  reason: string;
  peerId: string;
  stream?: 'received' | 'sent';
  videoBitrate?: number;
  videoJitter?: number;
  receivedVideoJitter?: number;
  receivedVideoPacketLossRate?: number;
  videoFramesPerSecond?: number;
  videoRoundTripTime?: number;
  sentVideoJitter?: number;
  sentVideoPacketLossRate?: number;
  qualityLimitationReason?: string;
  videoFreezeCount?: number;
  videoTotalFreezesDuration?: number;
}

export interface TrainingForkerQualityMonitorPeerStatus {
  peerId: string;
  qualityDegraded: boolean;
  qualityDegradedReason: string | null;
  /** Latest metric values for the hidden debug health tab. */
  videoBitrate?: number;
  videoFramesPerSecond?: number;
  videoFreezeCount?: number;
  videoTotalFreezesDuration?: number;
  receivedVideoPacketLossRate?: number;
  receivedVideoJitter?: number;
  sentVideoPacketLossRate?: number;
  sentVideoJitter?: number;
  qualityLimitationReason?: string;
  /** Per-signal consecutive bad poll counts (debounce before pause). */
  receivedFreezeConsecutivePolls: number;
  receivedFreezeStreakTotalMs: number;
  receivedPacketLossConsecutivePolls: number;
  receivedJitterConsecutivePolls: number;
  sentEncoderLimitationConsecutivePolls: number;
  sentPacketLossConsecutivePolls: number;
  sentJitterConsecutivePolls: number;
}

export interface TrainingForkerState {
  enabled: boolean;
  active: boolean;
  wasActiveDuringCall: boolean;
  /** When true, forking is blocked for the remainder of this SIP call. */
  qualityDegraded: boolean;
  qualityDegradedReason: string | null;
  qualityMonitorInGracePeriod: boolean;
  /** Live evaluator snapshots keyed by SIP peer id (debug menu). */
  qualityMonitorPeers: Record<string, TrainingForkerQualityMonitorPeerStatus>;
}

// Disable training forker by default when running under a test environment.
//  - Vitest/Node does not provide WebRTC APIs (RTCPeerConnection, etc.),
//    causing unhandled promise rejections when the forker logic attempts to
//    create peer connections during integration tests.
//  - Disabling here avoids needing wide mocks and eliminates noisy errors.
//  - Individual tests that require forker behavior can explicitly dispatch
//    enableForking().
export const initialTrainingForkerState: TrainingForkerState = {
  enabled: false,
  active: false,
  wasActiveDuringCall: false,
  qualityDegraded: false,
  qualityDegradedReason: null,
  qualityMonitorInGracePeriod: false,
  qualityMonitorPeers: {},
};

export const trainingForkerSlice = createSlice({
  name: 'training-forker',
  initialState: initialTrainingForkerState,
  reducers: {
    enableForking: (state) => {
      state.enabled = true;
    },
    disableForking: (state) => {
      state.enabled = false;
    },

    startedForking: (state) => {
      console.log('trainingForkerSlice: startedForking called');
      state.active = true;
      state.wasActiveDuringCall = true;
    },
    stoppedForking: (state) => {
      console.log('trainingForkerSlice: stoppedForking called');
      state.active = false;
    },
    forkingQualityDegraded: (
      state,
      action: { payload: ForkingQualityDegradedPayload }
    ) => {
      if (state.qualityDegraded) {
        return;
      }

      state.qualityDegraded = true;
      state.qualityDegradedReason = action.payload.reason;
    },
    forkingQualityDegradedReset: (state) => {
      state.qualityDegraded = false;
      state.qualityDegradedReason = null;
    },
    /** Replaces peer snapshots; drops peers no longer in `activePeerIds`. */
    syncTrainingForkerQualityMonitorPeers: (
      state,
      action: {
        payload: {
          activePeerIds: string[];
          isInGracePeriod: boolean;
          peers: Record<string, TrainingForkerQualityMonitorPeerStatus>;
        };
      }
    ) => {
      const currentPeers = state.qualityMonitorPeers ?? {};
      const nextPeers: Record<string, TrainingForkerQualityMonitorPeerStatus> =
        {};

      for (const peerId of action.payload.activePeerIds) {
        const nextPeer = action.payload.peers[peerId];
        const existingPeer = currentPeers[peerId];

        if (nextPeer) {
          nextPeers[peerId] = nextPeer;
        } else if (existingPeer) {
          nextPeers[peerId] = existingPeer;
        }
      }

      state.qualityMonitorPeers = nextPeers;
      state.qualityMonitorInGracePeriod = action.payload.isInGracePeriod;
    },
    clearTrainingForkerQualityMonitorPeers: (state) => {
      state.qualityMonitorPeers = {};
      state.qualityMonitorInGracePeriod = false;
    },
    resetTrainingForkerForNewSipCall: (state) => {
      // Clears the quality latch so forking can start on the new SIP session.
      state.wasActiveDuringCall = false;
      state.qualityDegraded = false;
      state.qualityDegradedReason = null;
      state.qualityMonitorInGracePeriod = false;
      state.qualityMonitorPeers = {};
    },
    resetTrainingForker: (state) => {
      return { ...initialTrainingForkerState, enabled: state.enabled };
    },
  },
});

export const {
  enableForking,
  disableForking,
  startedForking,
  stoppedForking,
  forkingQualityDegraded,
  forkingQualityDegradedReset,
  syncTrainingForkerQualityMonitorPeers,
  clearTrainingForkerQualityMonitorPeers,
  resetTrainingForkerForNewSipCall,
  resetTrainingForker,
} = trainingForkerSlice.actions;

export const trainingForkerReducer = trainingForkerSlice.reducer;

export const trainingForkerSelector = ({ trainingForker }: RootState) =>
  trainingForker;

export const isTrainingForkerEnabledSelector = ({
  trainingForker,
}: RootState) => trainingForker.enabled;
